import { Box, Button, Dialog, Flex, Separator, Switch, Text, TextField, IconButton, Select } from '@radix-ui/themes';
import { MdOutlineEdit } from 'react-icons/md';
import React, { useState, useEffect } from 'react';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import axiosInstance from '../../config/axiosConfig';
import { createPortal } from 'react-dom';
import { useFormik } from 'formik';
import { createInstitutionSchema, editInstitutionSchema } from '../../Schema';
import DialogCloseButton from './DialogCloseButton';

const styles = `
  .input-error .rt-TextFieldInput,
  .input-error .rt-SelectTrigger {
    border-color: red;
  }
`;

const CreateInstitution = ({ 
  initialValues = null, 
  onInstitutionCreated, 
  showToast, 
  institutionTypes = [], 
  licenceTypes = [] 
}) => {
  const isEditMode = !!initialValues;
  const [isOpen, setIsOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      email: "",
      username: "",
      phone: "",
      type: institutionTypes.length > 0 ? institutionTypes[0].key : "CareHome",
      rooms: licenceTypes.length > 0 ? parseInt(licenceTypes[0]?.key || "10") : 10,
      isSensorNeeded: true,
      Security: false,
      Housekeeping: false,
      "F&B": false,
    },
    validationSchema: isEditMode ? editInstitutionSchema : createInstitutionSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      console.log("Form submitted", values);
      setSubmitting(true);
      try {
        const institutionData = {
          institutionModel: {
            token: isEditMode ? initialValues.token : null,
            name: values.name,
            type: values.type,
            address: values.address,
            email: values.email,
            countryCode: values.phone ? values.phone.slice(0, 3).replace('+', '') : 'EG',
            phoneNo: values.phone,
            rooms: values.rooms,
            userName: values.username,
            firstName: values.name,
            lastName: values.name,
            isActive: isEditMode ? initialValues.isActive ?? true : true,
            isDeleted: isEditMode ? initialValues.isDeleted ?? false : false,
            isEmailConfirmed: isEditMode ? initialValues.isEmailConfirmed ?? false : false,
            isSensorNeeded: values.isSensorNeeded,
            isSecurity: values.Security || false,
            isHousekeeping: values.Housekeeping || false,
            isFnb: values["F&B"] || false,
          },
          admin: {
            userName: values.username,
            email: values.email,
            firstName: values.name,
            lastName: values.name,
            role: "Admin",
            ...(isEditMode && { token: initialValues.token }),
          },
        };

        console.log("Sending data:", institutionData);
        const endpoint = isEditMode ? '/institution/update' : '/institution/registration';
        console.log("Using endpoint:", endpoint);
        const response = await axiosInstance.post(endpoint, institutionData);

        if (response.data.result) {
          const successMessage = `Institution ${isEditMode ? 'updated' : 'created'} successfully`;
          console.log(successMessage);
          if (showToast) showToast(successMessage);
          if (onInstitutionCreated) await onInstitutionCreated();
          setIsOpen(false);
          resetForm();
        } else {
          const errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} institution`;
          console.error(`${errorMessage}:`, response.data);
          if (showToast) showToast(errorMessage);
        }
      } catch (error) {
        const errorMessage = `Error ${isEditMode ? 'updating' : 'creating'} institution`;
        console.error(`${errorMessage}:`, error);
        if (showToast) showToast(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue, resetForm, setValues } = formik;

  useEffect(() => {
    if (isEditMode && isOpen && initialValues) {
      setValues({
        name: initialValues.name || "",
        address: initialValues.address || "",
        email: initialValues.email || "",
        username: initialValues.userName || "",
        phone: initialValues.phoneNo || "",
        type: initialValues.type || (institutionTypes.length > 0 ? institutionTypes[0].key : ""),
        rooms: initialValues.rooms || (licenceTypes.length > 0 ? parseInt(licenceTypes[0]?.key || "0") : 0),
        isSensorNeeded: initialValues.isSensorNeeded ?? true,
        Security: initialValues.isSecurity || false,
        Housekeeping: initialValues.isHousekeeping || false,
        "F&B": initialValues.isFnb || false,
      });
    }
  }, [isOpen, isEditMode, initialValues, institutionTypes, licenceTypes, setValues]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const submitForm = (e) => {
    e.preventDefault();
    console.log("Manual submit triggered");
    formik.validateForm().then(errors => {
      console.log("Validation errors:", errors);
      if (Object.keys(errors).length === 0) {
        formik.handleSubmit();
      } else {
        Object.keys(formik.values).forEach(field => {
          formik.setFieldTouched(field, true);
        });
      }
    });
  };

  const dialogContent = isOpen ? (
    <Dialog.Content maxWidth="450px">
      <DialogCloseButton onClose={() => setIsOpen(false)} />
      <Dialog.Title>{isEditMode ? "Manage Institution Details" : "Create New Institution"}</Dialog.Title>
      <Dialog.Description size="2" mb="4">
        {isEditMode ? "Modify the institution's details." : "Fill in the details for the new institution."}
      </Dialog.Description>

      <form onSubmit={handleSubmit} id="institutionForm">
        <style>{styles}</style>
        <Flex direction="column" gap="3">
          <label className="w-[100%]">
            <Text as="div" size="2" mb="1" weight="bold">Institution Name</Text>
            <TextField.Root
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter institution name"
              className={errors.name && touched.name ? "input-error" : ""}
            />
            {errors.name && touched.name && <Text size="1" color="red" mt="1">{errors.name}</Text>}
          </label>

          <label className="w-[100%]">
            <Text as="div" size="2" mb="1" weight="bold">Institution Address</Text>
            <TextField.Root
              id="address"
              name="address"
              value={values.address}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter institution address"
              className={errors.address && touched.address ? "input-error" : ""}
            />
            {errors.address && touched.address && <Text size="1" color="red" mt="1">{errors.address}</Text>}
          </label>

          <Flex gap="3">
            <label className="w-[50%]">
              <Text as="div" size="2" mb="1" weight="bold">Phone Number</Text>
              <PhoneInput
                country="eg"
                enableSearch={true}
                value={values.phone}
                onChange={(value) => {
                  const formattedPhone = value.startsWith('+') ? value : `+${value}`;
                  setFieldValue('phone', formattedPhone);
                }}
                onBlur={() => handleBlur({ target: { name: 'phone' } })}
                inputStyle={{
                  width: "100%",
                  height: "33px",
                  fontSize: "14px",
                  paddingLeft: "55px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                  borderRadius: "6px",
                  border: errors.phone && touched.phone ? "1px solid red" : "1px solid #ccc",
                }}
              />
              {errors.phone && touched.phone && <Text size="1" color="red" mt="1">{errors.phone}</Text>}
            </label>

            <label className="w-[50%]">
              <Text as="div" size="2" mb="1" weight="bold">Institution Email</Text>
              <TextField.Root
                id="email"
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter institution email"
                readOnly={isEditMode}
                disabled={isEditMode}
                className={errors.email && touched.email ? "input-error" : ""}
              />
              {errors.email && touched.email && <Text size="1" color="red" mt="1">{errors.email}</Text>}
            </label>
          </Flex>

          <Flex gap="3">
            <label className="w-[100%]">
              <Text as="div" size="2" mb="1" weight="bold">Username</Text>
              <TextField.Root
                id="username"
                name="username"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter username"
                disabled={isEditMode}
                className={errors.username && touched.username ? "input-error" : ""}
              />
              {errors.username && touched.username && <Text size="1" color="red" mt="1">{errors.username}</Text>}
            </label>
          </Flex>

          <Flex gap="3">
            <label className="w-[50%]">
              <Text as="div" size="2" mb="1" weight="bold">Institution Type</Text>
              <Select.Root
                value={values.type}
                onValueChange={(value) => setFieldValue('type', value)}
                onBlur={() => handleBlur({ target: { name: 'type' } })}
              >
                <Select.Trigger
                  style={{ width: '100%' }}
                  className={errors.type && touched.type ? "input-error" : ""}
                />
                <Select.Content position="popper">
                  <Select.Group>
                    {institutionTypes.map(type => (
                      <Select.Item key={type.key} value={type.key}>
                        {type.display}
                      </Select.Item>
                    ))}
                  </Select.Group>
                </Select.Content>
              </Select.Root>
              {errors.type && touched.type && <Text size="1" color="red" mt="1">{errors.type}</Text>}
            </label>
            <label className="w-[50%]">
              <Text as="div" size="2" mb="1" weight="bold">Licence Type</Text>
              <Select.Root
                value={values.rooms.toString()}
                onValueChange={(value) => setFieldValue('rooms', parseInt(value))}
                onBlur={() => handleBlur({ target: { name: 'rooms' } })}
              >
                <Select.Trigger
                  style={{ width: '100%' }}
                  className={errors.rooms && touched.rooms ? "input-error" : ""}
                />
                <Select.Content position="popper">
                  <Select.Group>
                    {licenceTypes.map(licence => (
                      <Select.Item key={licence.key} value={licence.key.toString()}>
                        {licence.display}
                      </Select.Item>
                    ))}
                  </Select.Group>
                </Select.Content>
              </Select.Root>
              {errors.rooms && touched.rooms && <Text size="1" color="red" mt="1">{errors.rooms}</Text>}
            </label>
          </Flex>

          <Text className="mt-3" as="div" size="2" mb="1" weight="bold">
            Enable Additional Roles
          </Text>
          <Flex gap="3" wrap="wrap">
            {[
              { key: "Security", display: "Security" },
              { key: "Housekeeping", display: "Housekeeping" },
              { key: "F&B", display: "F&B" },
            ].map((role, index) => (
              <React.Fragment key={role.key}>
                <Text as="label" size="2">
                  <Flex gap="2">
                    <Switch
                      size="1"
                      checked={values[role.key]}
                      onCheckedChange={(checked) => setFieldValue(role.key, checked)}
                    />
                    {role.display}
                  </Flex>
                </Text>
                {index < 2 && <Separator orientation="vertical" />}
              </React.Fragment>
            ))}
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={submitForm}
            >
              {isEditMode ? "Update" : "Save"}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Dialog.Content>
  ) : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      {isEditMode ? (
        <Dialog.Trigger>
          <IconButton variant="outline" onClick={() => setIsOpen(true)}>
            <MdOutlineEdit width="18" height="18" />
          </IconButton>
        </Dialog.Trigger>
      ) : (
        <Dialog.Trigger>
          <Button onClick={() => setIsOpen(true)}>Create New Institution</Button>
        </Dialog.Trigger>
      )}
      {isOpen && createPortal(dialogContent, document.body)}
    </Dialog.Root>
  );
};

export default CreateInstitution;